import * as React from "react"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface DisabledReasonTooltipProps {
  /**
   * Why the control is disabled, or null when it is enabled. While set, the
   * control is wrapped in a focusable span (a disabled button gets no hover
   * or focus events of its own) that names the reason through
   * aria-describedby.
   */
  reason: React.ReactNode | null | undefined
  /** Optional tooltip for the ENABLED control. No wrapper span is added. */
  hint?: React.ReactNode
  /** The control: one element (a Button, a Select). */
  children: React.ReactElement
}

/**
 * A control with its disabled reason on hover and focus. Enabled with no
 * hint, it renders the control unchanged.
 */
export function DisabledReasonTooltip({ reason, hint, children }: DisabledReasonTooltipProps) {
  const reasonId = React.useId()
  const disabled = reason !== null && reason !== undefined && reason !== false && reason !== ""

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex" aria-describedby={reasonId}>
            {children}
            <span id={reasonId} className="sr-only">
              {reason}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
    )
  }
  if (hint === null || hint === undefined || hint === false || hint === "") return children
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  )
}
